import React, { useState, useEffect } from 'react';
import { Form, Input, Card, Table, message, Button, Space, Typography, Row, Col, Statistic, Popconfirm } from 'antd';
import { SearchOutlined, UserAddOutlined, EditOutlined, DeleteOutlined, UserOutlined, PhoneOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/constants';
import './customer.css';

const Customer = () => {
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const [searchText, setSearchText] = useState('');

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_CUSTOMER);
      if (response.data.success && Array.isArray(response.data.data)) {
        const sortedData = response.data.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setCustomers(sortedData);
        setFilteredCustomers(sortedData);
      } else {
        throw new Error('Data format is incorrect');
      }
    } catch (error) {
      message.info('Failed to fetch customers.');
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value) {
      setFilteredCustomers(customers);
      return;
    }

    const filtered = customers.filter(customer =>
      customer.name.toLowerCase().includes(value.toLowerCase()) ||
      customer.number.includes(value)
    );
    setFilteredCustomers(filtered);
  };

  const checkNameExists = (name) => {
    return customers.some(customer => customer.name.toLowerCase() === name.toLowerCase());
  };

  const onFinish = async (values) => {
    try {
      if (!editingKey && checkNameExists(values.name)) {
        message.error('Customer with this name already exists!');
        return;
      }

      if (editingKey) {
        await axios.post(API_ENDPOINTS.UPDATE_CUSTOMER, { ...values, name: editingKey });
        message.success('Customer updated successfully!');
      } else {
        await axios.post(API_ENDPOINTS.ADD_CUSTOMER, values);
        message.success('Customer added successfully!');
      }

      form.resetFields();
      setEditingKey('');
      fetchCustomers();
    } catch (error) {
      message.error('Failed to save customer.');
    }
  };

  const handleEdit = (record) => {
    form.setFieldsValue(record);
    setEditingKey(record.name);
  };

  const handleDelete = async (name) => {
    try {
      const response = await axios.post(API_ENDPOINTS.DELETE_CUSTOMER, { name }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success('Customer deleted successfully!');
        fetchCustomers();
      } else {
        message.error('Failed to delete customer: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      message.error('Failed to delete customer.');
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'serial',
      key: 'serial',
      width: 50,
      render: (_, __, index) => (
        <span style={{ fontWeight: 500, color: '#666' }}>{index + 1}</span>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Phone Number',
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={editingKey !== ''}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Customer"
            description="Are you sure you want to delete this customer?"
            onConfirm={() => handleDelete(record.name)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const nameValidationRules = [
    { required: true, message: 'Please enter the customer name' },
    () => ({
      validator(_, value) {
        if (!value || editingKey || !checkNameExists(value)) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Customer with this name already exists!'));
      },
    }),
  ];

  return (
    <div className="fade-in">
      <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  TOTAL REGISTERED CUSTOMERS
                </Typography.Text>
                <Statistic
                  value={customers.length}
                  valueStyle={{ color: '#0F172A', fontWeight: 800, fontSize: '24px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#EEF2FF', color: '#4F46E5' }}>
                <UserOutlined />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  FILTERED / MATCHED
                </Typography.Text>
                <Statistic
                  value={filteredCustomers.length}
                  valueStyle={{ color: '#10B981', fontWeight: 800, fontSize: '24px' }}
                />
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#ECFDF5', color: '#10B981' }}>
                <SearchOutlined />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
            <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
              <div>
                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  SEARCH FILTER STATE
                </Typography.Text>
                <Typography.Title level={4} style={{ margin: 0, color: searchText ? '#F59E0B' : '#64748B', fontWeight: 700 }}>
                  {searchText ? `Searching "${searchText}"` : 'All Customers'}
                </Typography.Title>
              </div>
              <div className="stat-icon-wrapper" style={{ background: '#FFFBEB', color: '#F59E0B' }}>
                <UserAddOutlined />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={9}>
          <Card 
            title={
              <Space size="middle">
                <UserAddOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>
                  {editingKey ? "Edit Customer Record" : "Add New Customer"}
                </span>
              </Space>
            }
            style={{ borderRadius: '16px' }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
            >
              <Form.Item
                name="name"
                label="Customer Name"
                rules={nameValidationRules}
              >
                <Input 
                  placeholder="Full customer or business name" 
                  prefix={<UserOutlined style={{ color: '#94A3B8' }} />}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="number"
                label="Phone Number"
                rules={[{ required: true, message: 'Please enter the phone number' }]}
              >
                <Input 
                  placeholder="Primary contact phone number" 
                  prefix={<PhoneOutlined style={{ color: '#94A3B8' }} />}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="address"
                label="Street Address"
              >
                <Input.TextArea 
                  placeholder="Customer billing / delivery address" 
                  rows={3}
                  style={{ borderRadius: '10px' }}
                />
              </Form.Item>
              <Form.Item
                name="city"
                label="City / Town"
              >
                <Input 
                  placeholder="e.g. Borsad, Anand" 
                  size="large"
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, marginTop: '20px' }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  {editingKey && (
                    <Button 
                      onClick={() => {
                        form.resetFields();
                        setEditingKey('');
                      }}
                      size="large"
                      style={{ borderRadius: '10px' }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    icon={editingKey ? <EditOutlined /> : <UserAddOutlined />}
                    size="large"
                    style={{ borderRadius: '10px' }}
                  >
                    {editingKey ? "Save Changes" : "Create Customer"}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        
        <Col xs={24} lg={15}>
          <Card 
            title={
              <Space size="middle">
                <UserOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Customer Directory</span>
              </Space>
            }
            extra={
              <Input.Search
                placeholder="Search name or phone..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 260 }}
                allowClear
                size="large"
              />
            }
            style={{ borderRadius: '16px' }}
          >
            <Table
              columns={columns}
              dataSource={filteredCustomers}
              rowKey="name"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} customers`
              }}
              scroll={{ x: 700 }}
              size="middle"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Customer;

